import { useState, useRef, useCallback } from "react";
import { Modal, Stack, NumberInput, Button, Group, Text, Switch } from "@mantine/core";
import logoBackgroundSvg from "../../assets/logo-background.svg";

interface Slot {
	image: string | null;
}

type DeviceType = "iphone" | "ipad" | "android" | "android-tablet";

interface DeviceConfig {
	label: string;
	canvasW: number;
	canvasH: number;
	deviceW: number;
	deviceH: number;
	frameRadius: number;
	frameThickness: number;
	islandW: number;
	islandH: number;
	islandR: number;
	islandY: number;
	cameraType?: "island" | "punch-hole" | "none";
}

const DEVICES: Record<DeviceType, DeviceConfig> = {
	iphone: {
		label: 'iPhone 6.5" (1284x2778)',
		canvasW: 1284,
		canvasH: 2778,
		deviceW: 1000,
		deviceH: 2050,
		frameRadius: 120,
		frameThickness: 26,
		islandW: 260,
		islandH: 64,
		islandR: 32,
		islandY: 28,
		cameraType: "island",
	},
	ipad: {
		label: 'iPad 13" (2064x2752)',
		canvasW: 2064,
		canvasH: 2752,
		deviceW: 1760,
		deviceH: 2400,
		frameRadius: 80,
		frameThickness: 30,
		islandW: 260,
		islandH: 64,
		islandR: 32,
		islandY: 28,
		cameraType: "island",
	},
	android: {
		label: 'Android Phone (1080x2400)',
		canvasW: 1080,
		canvasH: 2400,
		deviceW: 860,
		deviceH: 1780,
		frameRadius: 90,
		frameThickness: 22,
		islandW: 40,
		islandH: 40,
		islandR: 20,
		islandY: 20,
		cameraType: "punch-hole",
	},
	"android-tablet": {
		label: 'Android Tablet (1600x2560)',
		canvasW: 1600,
		canvasH: 2560,
		deviceW: 1360,
		deviceH: 2200,
		frameRadius: 60,
		frameThickness: 28,
		islandW: 36,
		islandH: 36,
		islandR: 18,
		islandY: 18,
		cameraType: "punch-hole",
	},
};

const SLOT_COUNT = 6;

function roundRectPath(
	ctx: CanvasRenderingContext2D,
	x: number,
	y: number,
	w: number,
	h: number,
	r: number,
): void {
	ctx.beginPath();
	ctx.moveTo(x + r, y);
	ctx.lineTo(x + w - r, y);
	ctx.arcTo(x + w, y, x + w, y + r, r);
	ctx.lineTo(x + w, y + h - r);
	ctx.arcTo(x + w, y + h, x + w - r, y + h, r);
	ctx.lineTo(x + r, y + h);
	ctx.arcTo(x, y + h, x, y + h - r, r);
	ctx.lineTo(x, y + r);
	ctx.arcTo(x, y, x + r, y, r);
	ctx.closePath();
}

function drawMockupToCanvas(
	ctx: CanvasRenderingContext2D,
	img: HTMLImageElement,
	device: DeviceType,
	offsetX: number,
	offsetY: number,
	skipBackground = false,
): void {
	const cfg = DEVICES[device];
	const CW = cfg.canvasW,
		CH = cfg.canvasH;

	ctx.save();
	ctx.translate(offsetX, offsetY);

	// Simple solid dark background
	if (!skipBackground) {
		ctx.fillStyle = "#0a1628";
		ctx.fillRect(0, 0, CW, CH);
	}

	const phoneW = cfg.deviceW,
		phoneH = cfg.deviceH;
	const phoneX = (CW - phoneW) / 2,
		phoneY = (CH - phoneH) / 2;
	const frameRadius = cfg.frameRadius,
		frameThickness = cfg.frameThickness;

	// Device frame shadow
	ctx.save();
	ctx.shadowColor = "rgba(0,0,0,0.5)";
	ctx.shadowBlur = 60;
	ctx.shadowOffsetY = 20;
	ctx.fillStyle = "#000";
	roundRectPath(ctx, phoneX, phoneY, phoneW, phoneH, frameRadius);
	ctx.fill();
	ctx.restore();

	// Device body
	ctx.fillStyle = "#1a1a1a";
	roundRectPath(ctx, phoneX, phoneY, phoneW, phoneH, frameRadius);
	ctx.fill();

	// Subtle edge highlight
	ctx.strokeStyle = "rgba(255,255,255,0.08)";
	ctx.lineWidth = 1.5;
	roundRectPath(
		ctx,
		phoneX + 1,
		phoneY + 1,
		phoneW - 2,
		phoneH - 2,
		frameRadius,
	);
	ctx.stroke();

	// Screen area
	const scrX = phoneX + frameThickness,
		scrY = phoneY + frameThickness;
	const scrW = phoneW - frameThickness * 2,
		scrH = phoneH - frameThickness * 2,
		scrRadius = frameRadius - 10;

	// Draw screenshot
	ctx.save();
	roundRectPath(ctx, scrX, scrY, scrW, scrH, scrRadius);
	ctx.clip();
	ctx.drawImage(img, scrX, scrY, scrW, scrH);
	ctx.restore();

	// Camera cutout
	const cameraType = cfg.cameraType || "island";
	if (cameraType === "island") {
		ctx.fillStyle = "#000";
		roundRectPath(
			ctx,
			CW / 2 - cfg.islandW / 2,
			scrY + cfg.islandY,
			cfg.islandW,
			cfg.islandH,
			cfg.islandR,
		);
		ctx.fill();
	} else if (cameraType === "punch-hole") {
		ctx.fillStyle = "#000";
		ctx.beginPath();
		ctx.arc(
			CW / 2,
			scrY + cfg.islandY + cfg.islandH / 2,
			cfg.islandW / 2,
			0,
			Math.PI * 2,
		);
		ctx.fill();
	}

	// Side buttons (iPhone only)
	if (device === "iphone") {
		ctx.fillStyle = "#222";
		ctx.fillRect(phoneX - 6, phoneY + 300, 6, 100);
		ctx.fillRect(phoneX - 6, phoneY + 430, 6, 100);
		ctx.fillRect(phoneX + phoneW, phoneY + 370, 6, 140);
	}

	// Side buttons (Android phone)
	if (device === "android") {
		ctx.fillStyle = "#222";
		ctx.fillRect(phoneX + phoneW, phoneY + 340, 6, 120);
		ctx.fillRect(phoneX + phoneW, phoneY + 490, 6, 80);
	}

	ctx.restore();
}

async function exportMockup(
	slot: Slot,
	idx: number,
	device: DeviceType,
): Promise<void> {
	if (!slot.image) return;
	const cfg = DEVICES[device];
	const CW = cfg.canvasW,
		CH = cfg.canvasH;
	const canvas = document.createElement("canvas");
	canvas.width = CW;
	canvas.height = CH;
	const ctx = canvas.getContext("2d");
	if (!ctx) return;

	await new Promise<void>((resolve) => {
		const img = new Image();
		img.onload = () => {
			drawMockupToCanvas(ctx, img, device, 0, 0);
			resolve();
		};
		img.src = slot.image!;
	});

	const prefixMap: Record<DeviceType, string> = {
		iphone: "store",
		ipad: "ipad",
		android: "android",
		"android-tablet": "android_tablet",
	};
	const prefix = prefixMap[device];
	canvas.toBlob((blob) => {
		if (!blob) return;
		const url = URL.createObjectURL(blob);
		const a = document.createElement("a");
		a.href = url;
		a.download = `${prefix}_mockup_${idx + 1}.png`;
		a.click();
		setTimeout(() => URL.revokeObjectURL(url), 3000);
	}, "image/png");
}

async function exportCombined(
	slots: Slot[],
	device: DeviceType,
	gap: number,
	edgePadding: number,
	transparentBg = false,
): Promise<void> {
	const loadedSlots = slots.filter((s) => s.image);
	if (loadedSlots.length === 0) return;

	const cfg = DEVICES[device];
	const count = loadedSlots.length;
	// step = phone body width + gap, so phones are exactly GAP apart edge-to-edge
	// totalW keeps the natural phoneMargin on both outer sides, plus optional edgePadding
	const step = cfg.deviceW + gap;
	const totalW = step * (count - 1) + cfg.canvasW + edgePadding * 2;
	const totalH = cfg.canvasH;

	const canvas = document.createElement("canvas");
	canvas.width = totalW;
	canvas.height = totalH;
	const ctx = canvas.getContext("2d");
	if (!ctx) return;

	// Fill background (skip for transparent export)
	if (!transparentBg) {
		ctx.fillStyle = "#0a1628";
		ctx.fillRect(0, 0, totalW, totalH);
	}

	const phoneMargin = (cfg.canvasW - cfg.deviceW) / 2;

	// Load all images and draw side by side with tight spacing
	for (let i = 0; i < loadedSlots.length; i++) {
		await new Promise<void>((resolve) => {
			const img = new Image();
			img.onload = () => {
				const offsetX = edgePadding + i * step;
				// Clip each phone to its own column so background fills don't
				// bleed over and overwrite adjacent phone bodies
				const clipLeft = i === 0 ? 0 : offsetX + phoneMargin - gap / 2;
				const clipRight = i === loadedSlots.length - 1
					? totalW
					: offsetX + phoneMargin + cfg.deviceW + gap / 2;
				ctx.save();
				ctx.beginPath();
				ctx.rect(clipLeft, 0, clipRight - clipLeft, totalH);
				ctx.clip();
				drawMockupToCanvas(ctx, img, device, offsetX, 0, transparentBg);
				ctx.restore();
				resolve();
			};
			img.src = loadedSlots[i].image!;
		});
	}

	const prefixMap: Record<DeviceType, string> = {
		iphone: "store",
		ipad: "ipad",
		android: "android",
		"android-tablet": "android_tablet",
	};
	const prefix = prefixMap[device];
	canvas.toBlob((blob) => {
		if (!blob) return;
		const url = URL.createObjectURL(blob);
		const a = document.createElement("a");
		a.href = url;
		a.download = `${prefix}_combined_${count}x.png`;
		a.click();
		setTimeout(() => URL.revokeObjectURL(url), 3000);
	}, "image/png");
}

interface PhonePreviewProps {
	imageUrl: string | null;
}

function PhonePreview({ imageUrl }: PhonePreviewProps) {
	return (
		<div
			style={{
				width: 180,
				height: 372,
				position: "relative",
				flexShrink: 0,
				background: "#0a1628",
				borderRadius: 20,
				display: "flex",
				alignItems: "center",
				justifyContent: "center",
				overflow: "hidden",
			}}
		>
			<div
				style={{
					width: 150,
					height: 320,
					background: "#1a1a1a",
					borderRadius: 28,
					position: "relative",
					boxShadow: "0 8px 30px rgba(0,0,0,0.5)",
					overflow: "hidden",
				}}
			>
				<div
					style={{
						position: "absolute",
						top: 5,
						left: 5,
						right: 5,
						bottom: 5,
						borderRadius: 24,
						overflow: "hidden",
						background: imageUrl ? "transparent" : "#1a1a1a",
					}}
				>
					{imageUrl ? (
						<img
							src={imageUrl}
							alt=""
							style={{
								width: "100%",
								height: "100%",
								objectFit: "cover",
								display: "block",
							}}
						/>
					) : (
						<div
							style={{
								width: "100%",
								height: "100%",
								display: "flex",
								alignItems: "center",
								justifyContent: "center",
							}}
						>
							<svg
								width="28"
								height="28"
								viewBox="0 0 24 24"
								fill="none"
								stroke="rgba(255,255,255,0.25)"
								strokeWidth="1.5"
							>
								<path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M17 8l-5-5-5 5M12 3v12" />
							</svg>
						</div>
					)}
					<div
						style={{
							position: "absolute",
							top: 8,
							left: "50%",
							transform: "translateX(-50%)",
							width: 50,
							height: 14,
							background: "#000",
							borderRadius: 10,
							zIndex: 10,
						}}
					/>
				</div>
			</div>
		</div>
	);
}

function MockupMaker() {
	const [slots, setSlots] = useState<Slot[]>(
		Array.from({ length: SLOT_COUNT }, () => ({ image: null })),
	);
	const [exporting, setExporting] = useState<number | null>(null);
	const [exportingCombined, setExportingCombined] = useState(false);
	const [device, setDevice] = useState<DeviceType>("iphone");
	const [combinedModalOpen, setCombinedModalOpen] = useState(false);
	const [combinedGap, setCombinedGap] = useState<number>(20);
	const [combinedEdgePadding, setCombinedEdgePadding] = useState<number>(0);
	const [combinedTransparentBg, setCombinedTransparentBg] = useState(false);
	const fileRefs = useRef<(HTMLInputElement | null)[]>([]);
	const handleFile = useCallback((idx: number, file: File | undefined) => {
		if (!file || !file.type.startsWith("image/")) return;
		const reader = new FileReader();
		reader.onload = (e) =>
			setSlots((prev) =>
				prev.map((s, i) =>
					i === idx
						? { ...s, image: e.target?.result as string }
						: s,
				),
			);
		reader.readAsDataURL(file);
	}, []);
	const handleExport = async (idx: number) => {
		setExporting(idx);
		try {
			await exportMockup(slots[idx], idx, device);
		} finally {
			setExporting(null);
		}
	};
	const anyLoaded = slots.some((s) => s.image);
	const allLoaded = slots.every((s) => s.image);
	const handleExportAll = async () => {
		for (let i = 0; i < slots.length; i++) {
			if (slots[i].image) {
				setExporting(i);
				await exportMockup(slots[i], i, device);
				await new Promise((r) => setTimeout(r, 500));
			}
		}
		setExporting(null);
	};

	const handleExportCombined = async () => {
		setCombinedModalOpen(false);
		setExportingCombined(true);
		try {
			await exportCombined(slots, device, combinedGap, combinedEdgePadding, combinedTransparentBg);
		} finally {
			setExportingCombined(false);
		}
	};

	const cfg = DEVICES[device];
	const loadedCount = slots.filter((s) => s.image).length;

	return (
		<div
			style={{
				minHeight: "100vh",
				background: "#0d1117",
				fontFamily:
					"'Helvetica Neue', Helvetica, Arial, sans-serif",
				color: "#e8eaed",
				padding: "32px 24px",
			}}
		>
			<div style={{ maxWidth: 1100, margin: "0 auto 36px" }}>
				<div
					style={{
						display: "flex",
						alignItems: "center",
						justifyContent: "space-between",
						flexWrap: "wrap",
						gap: 16,
					}}
				>
					<div>
						<h1
							style={{
								margin: 0,
								fontSize: 22,
								fontWeight: 700,
								letterSpacing: "-0.5px",
							}}
						>
							App Store Mockup Generator
						</h1>
						<p
							style={{
								margin: "4px 0 0",
								fontSize: 13,
								color: "#6e7681",
							}}
						>
							Upload screenshots · Export at{" "}
							{cfg.canvasW}x{cfg.canvasH}px
						</p>
					</div>
					<div
						style={{
							display: "flex",
							alignItems: "center",
							gap: 12,
						}}
					>
						<div
							style={{
								display: "flex",
								borderRadius: 8,
								overflow: "hidden",
								border: "1px solid #30363d",
							}}
						>
							{(
								Object.keys(DEVICES) as DeviceType[]
							).map((d) => (
								<button
									key={d}
									onClick={() => setDevice(d)}
									style={{
										padding: "7px 16px",
										border: "none",
										background:
											device === d
												? "#fff"
												: "transparent",
										color:
											device === d
												? "#000"
												: "#8b949e",
										fontSize: 13,
										fontWeight: 600,
										cursor: "pointer",
									}}
								>
									{{ iphone: "iPhone", ipad: "iPad", android: "Android", "android-tablet": "Android Tablet" }[d]}
								</button>
							))}
						</div>
						<button
							onClick={handleExportAll}
							disabled={
								!allLoaded || exporting !== null
							}
							style={{
								padding: "10px 22px",
								borderRadius: 10,
								border: "none",
								cursor: allLoaded
									? "pointer"
									: "not-allowed",
								background: allLoaded
									? "#fff"
									: "#1e2530",
								color: allLoaded ? "#000" : "#3d4a5c",
								fontSize: 14,
								fontWeight: 600,
								display: "flex",
								alignItems: "center",
								gap: 8,
							}}
						>
							<svg
								width="15"
								height="15"
								viewBox="0 0 24 24"
								fill="none"
								stroke="currentColor"
								strokeWidth="2.2"
							>
								<path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M7 10l5 5 5-5M12 15V3" />
							</svg>
							Export All 6
						</button>
						<button
							onClick={() => anyLoaded && setCombinedModalOpen(true)}
							disabled={
								!anyLoaded || exporting !== null || exportingCombined
							}
							style={{
								padding: "10px 22px",
								borderRadius: 10,
								border: "1px solid #30363d",
								cursor: anyLoaded
									? "pointer"
									: "not-allowed",
								background: anyLoaded
									? "transparent"
									: "#1e2530",
								color: anyLoaded ? "#e8eaed" : "#3d4a5c",
								fontSize: 14,
								fontWeight: 600,
								display: "flex",
								alignItems: "center",
								gap: 8,
							}}
						>
							{exportingCombined ? (
								<>
									<span
										style={{
											display: "inline-block",
											animation: "spin 1s linear infinite",
										}}
									>
										&#x27F3;
									</span>
									Exporting...
								</>
							) : (
								<>
									<svg
										width="15"
										height="15"
										viewBox="0 0 24 24"
										fill="none"
										stroke="currentColor"
										strokeWidth="2.2"
									>
										<path d="M4 6h16M4 12h16M4 18h16" />
									</svg>
									Combined ({loadedCount})
								</>
							)}
						</button>
					</div>
				</div>
			</div>
			<div
				style={{
					maxWidth: 1100,
					margin: "0 auto",
					display: "grid",
					gridTemplateColumns:
						"repeat(auto-fill, minmax(320px, 1fr))",
					gap: 20,
				}}
			>
				{slots.map((slot, idx) => (
					<div
						key={idx}
						style={{
							background: "#161b22",
							border: "1px solid #21262d",
							borderRadius: 16,
							overflow: "hidden",
						}}
					>
						<div
							style={{
								padding: "20px",
								display: "flex",
								alignItems: "center",
								gap: 20,
								cursor: !slot.image ? "pointer" : "default",
							}}
							onClick={() =>
								!slot.image &&
								fileRefs.current[idx]?.click()
							}
							onDragOver={(e) => e.preventDefault()}
							onDrop={(e) => {
								e.preventDefault();
								handleFile(idx, e.dataTransfer.files[0]);
							}}
						>
							<PhonePreview imageUrl={slot.image} />
							<div style={{ flex: 1, minWidth: 0 }}>
								<div
									style={{
										fontSize: 11,
										color: "#6e7681",
										marginBottom: 8,
										fontWeight: 600,
										textTransform: "uppercase",
										letterSpacing: "0.08em",
									}}
								>
									Screen {idx + 1}
								</div>
								{!slot.image ? (
									<div
										style={{
											border: "1.5px dashed #30363d",
											borderRadius: 10,
											padding: "18px 14px",
											textAlign: "center",
											color: "#6e7681",
											fontSize: 13,
										}}
									>
										Click or drop screenshot
									</div>
								) : (
									<div
										style={{
											border: "1.5px dashed #30363d",
											borderRadius: 10,
											padding: "10px 14px",
											textAlign: "center",
											color: "#8b949e",
											fontSize: 12,
											cursor: "pointer",
										}}
										onClick={() =>
											fileRefs.current[idx]?.click()
										}
									>
										Loaded - Click to replace
									</div>
								)}
							</div>
							<input
								ref={(el) => {
									fileRefs.current[idx] = el;
								}}
								type="file"
								accept="image/*"
								style={{ display: "none" }}
								onChange={(e) =>
									handleFile(
										idx,
										e.target.files?.[0],
									)
								}
							/>
						</div>
						<div
							style={{
								padding: "12px 20px 16px",
								borderTop: "1px solid #21262d",
							}}
						>
							<button
								onClick={() => handleExport(idx)}
								disabled={
									!slot.image || exporting !== null
								}
								style={{
									width: "100%",
									padding: "9px 0",
									borderRadius: 9,
									border: "none",
									background: slot.image
										? "#fff"
										: "#1e2530",
									color: slot.image
										? "#000"
										: "#3d4a5c",
									fontSize: 13,
									fontWeight: 600,
									cursor: slot.image
										? "pointer"
										: "not-allowed",
									display: "flex",
									alignItems: "center",
									justifyContent: "center",
									gap: 7,
								}}
							>
								{exporting === idx ? (
									<>
										<span
											style={{
												display: "inline-block",
												animation:
													"spin 1s linear infinite",
											}}
										>
											&#x27F3;
										</span>{" "}
										Exporting...
									</>
								) : (
									<>
										<svg
											width="13"
											height="13"
											viewBox="0 0 24 24"
											fill="none"
											stroke="currentColor"
											strokeWidth="2.2"
										>
											<path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M7 10l5 5 5-5M12 15V3" />
										</svg>{" "}
										Export PNG
									</>
								)}
							</button>
						</div>
					</div>
				))}
			</div>
			<div
				style={{
					maxWidth: 1100,
					margin: "24px auto 0",
					padding: "14px 18px",
					background: "#161b22",
					borderRadius: 10,
					border: "1px solid #21262d",
					fontSize: 12,
					color: "#6e7681",
				}}
			>
				<strong style={{ color: "#8b949e" }}>
					Export resolution:
				</strong>{" "}
				iPhone: 1284x2778px · iPad: 2064x2752px · Android: 1080x2400px · Android Tablet: 1600x2560px
				<br />
				<strong style={{ color: "#8b949e" }}>
					Combined:
				</strong>{" "}
				All loaded mockups side by side in a single image ({loadedCount} x {cfg.canvasW}px = {(cfg.deviceW + combinedGap) * (loadedCount - 1) + cfg.canvasW + combinedEdgePadding * 2}x{cfg.canvasH}px)
			</div>
			<style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
			<Modal
				opened={combinedModalOpen}
				onClose={() => setCombinedModalOpen(false)}
				title="Combined Export Settings"
				size="sm"
			>
				<Stack gap="md">
					<NumberInput
						label="Gap between phones (px)"
						description="Space between each phone edge-to-edge"
						value={combinedGap}
						onChange={(v) => setCombinedGap(typeof v === "number" ? v : 0)}
						min={0}
						max={500}
						step={10}
					/>
					<NumberInput
						label="Edge padding (px)"
						description="Extra space on the left and right sides"
						value={combinedEdgePadding}
						onChange={(v) => setCombinedEdgePadding(typeof v === "number" ? v : 0)}
						min={0}
						max={500}
						step={10}
					/>
					<Switch
						label="Transparent background"
						description="Export as PNG with no background behind phones"
						checked={combinedTransparentBg}
						onChange={(e) => setCombinedTransparentBg(e.currentTarget.checked)}
					/>
					<Text size="xs" c="dimmed">
						Output: {(cfg.deviceW + combinedGap) * (loadedCount - 1) + cfg.canvasW + combinedEdgePadding * 2}x{cfg.canvasH}px
					</Text>
					<Group justify="flex-end" gap="sm">
						<Button variant="default" onClick={() => setCombinedModalOpen(false)}>
							Cancel
						</Button>
						<Button color="dark" onClick={handleExportCombined}>
							Export
						</Button>
					</Group>
				</Stack>
			</Modal>
		</div>
	);
}

function FeaturedGraphicMaker() {
	const canvasRef = useRef<HTMLCanvasElement>(null);
	const [generated, setGenerated] = useState(false);

	const generate = useCallback(() => {
		const canvas = canvasRef.current;
		if (!canvas) return;
		const W = 1024, H = 500;
		canvas.width = W;
		canvas.height = H;
		const ctx = canvas.getContext("2d");
		if (!ctx) return;

		const img = new Image();
		const svgBlob = new Blob([BRAIN_SVG], { type: "image/svg+xml" });
		const url = URL.createObjectURL(svgBlob);
		img.onload = () => {
			drawBrainBackground(ctx, W, H, img);
			URL.revokeObjectURL(url);

			// Text: App name
			ctx.fillStyle = "#fff";
			ctx.font = "bold 52px 'Helvetica Neue', Helvetica, Arial, sans-serif";
			ctx.textAlign = "center";
			ctx.textBaseline = "middle";

			ctx.shadowColor = "rgba(0,0,0,0.3)";
			ctx.shadowBlur = 20;
			ctx.shadowOffsetY = 4;
			ctx.fillText("Mental Robusthedstræning", W / 2, H / 2 - 30);

			ctx.font = "300 24px 'Helvetica Neue', Helvetica, Arial, sans-serif";
			ctx.shadowBlur = 10;
			ctx.fillStyle = "rgba(255,255,255,0.8)";
			ctx.fillText("Styrk din mentale robusthed", W / 2, H / 2 + 30);

			ctx.shadowColor = "transparent";
			ctx.shadowBlur = 0;
			ctx.shadowOffsetY = 0;

			setGenerated(true);
		};
		img.src = url;
	}, []);

	const download = useCallback(() => {
		const canvas = canvasRef.current;
		if (!canvas) return;
		canvas.toBlob((blob) => {
			if (!blob) return;
			const url = URL.createObjectURL(blob);
			const a = document.createElement("a");
			a.href = url;
			a.download = "featured_graphic_1024x500.png";
			a.click();
			setTimeout(() => URL.revokeObjectURL(url), 3000);
		}, "image/png");
	}, []);

	return (
		<div
			style={{
				maxWidth: 1100,
				margin: "40px auto 0",
				background: "#161b22",
				border: "1px solid #21262d",
				borderRadius: 16,
				padding: 24,
			}}
		>
			<div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
				<div>
					<h2 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: "#e8eaed" }}>
						Featured Graphic (Google Play)
					</h2>
					<p style={{ margin: "4px 0 0", fontSize: 13, color: "#6e7681" }}>
						1024x500px · Brand colors + brain pattern
					</p>
				</div>
				<div style={{ display: "flex", gap: 10 }}>
					<button
						onClick={generate}
						style={{
							padding: "9px 20px",
							borderRadius: 9,
							border: "1px solid #30363d",
							background: "transparent",
							color: "#e8eaed",
							fontSize: 13,
							fontWeight: 600,
							cursor: "pointer",
						}}
					>
						{generated ? "Regenerate" : "Generate"}
					</button>
					{generated && (
						<button
							onClick={download}
							style={{
								padding: "9px 20px",
								borderRadius: 9,
								border: "none",
								background: "#fff",
								color: "#000",
								fontSize: 13,
								fontWeight: 600,
								cursor: "pointer",
								display: "flex",
								alignItems: "center",
								gap: 7,
							}}
						>
							<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
								<path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M7 10l5 5 5-5M12 15V3" />
							</svg>
							Download PNG
						</button>
					)}
				</div>
			</div>
			<div style={{ borderRadius: 10, overflow: "hidden", border: "1px solid #21262d" }}>
				<canvas
					ref={canvasRef}
					width={1024}
					height={500}
					style={{ width: "100%", height: "auto", display: "block", background: "#0a1628" }}
				/>
			</div>
		</div>
	);
}

const BRAIN_SVG = `<svg width="350" height="189" viewBox="0 0 350 189" fill="#000000" xmlns="http://www.w3.org/2000/svg"><g clip-path="url(#clip0)"><path opacity="0.15" d="M284.01 38.987C284.01 56.2264 273.181 70.6492 258.807 74C258.03 74.1943 255.7 74.6313 254.583 73.1259C253.514 71.7176 253.514 68.464 253.514 68.464V14.0748C253.514 14.0748 253.174 7.51895 254.777 5.52792C256.719 3.19695 260.41 3.97394 261.138 4.11963C277.358 7.81032 284.01 21.9904 284.01 38.987ZM251.135 14.0748V68.464C251.135 68.464 251.135 71.7176 250.066 73.1259C248.95 74.6313 246.57 74.1943 245.842 74C231.468 70.6492 220.639 56.2264 220.639 38.987C220.639 22.0389 227.291 7.81032 243.511 4.16819C244.239 4.0225 247.978 3.24551 249.872 5.57648C251.475 7.51895 251.135 14.0748 251.135 14.0748ZM288.43 38.987C288.43 16.4543 278.135 -1.75635 252.397 -1.75635C222.532 -1.75635 216.365 16.5029 216.365 38.987C216.365 61.5196 232.487 79.7303 252.397 79.7303C272.307 79.7303 288.43 61.4711 288.43 38.987Z" fill="white"/><path opacity="0.2" d="M318.697 85.0917C318.697 106.643 304.86 124.569 286.51 128.782C285.508 128.978 282.5 129.565 281.096 127.704C279.692 125.941 279.692 121.924 279.692 121.924V54.0385C279.692 54.0385 279.291 45.8099 281.296 43.361C283.803 40.4222 288.516 41.4018 289.418 41.5977C310.174 46.2018 318.697 63.9325 318.697 85.0917ZM276.784 54.0385V121.826C276.784 121.826 276.784 125.843 275.38 127.606C273.976 129.467 270.968 128.88 269.966 128.684C251.616 124.471 237.779 106.545 237.779 84.9937C237.779 63.8345 246.302 46.1038 266.958 41.5977C267.86 41.4018 272.673 40.4222 275.079 43.361C277.185 45.8099 276.784 54.0385 276.784 54.0385ZM324.313 85.0917C324.313 56.9773 311.177 34.2507 278.288 34.2507C240.185 34.2507 232.264 56.9773 232.264 85.0917C232.264 113.206 252.92 135.933 278.288 135.933C303.657 135.933 324.313 113.108 324.313 85.0917Z" fill="white"/><path opacity="0.35" d="M338.968 23.0121C338.968 45.0169 324.97 63.3209 306.407 67.6219C305.392 67.8219 302.349 68.422 300.929 66.5216C299.509 64.7212 299.509 60.6203 299.509 60.6203V-8.69477C299.509 -8.69477 299.103 -17.0966 301.132 -19.5971C303.668 -22.5978 308.435 -21.5976 309.348 -21.3975C330.346 -16.6965 338.968 1.40743 338.968 23.0121ZM296.567 -8.69477V60.5203C296.567 60.5203 296.567 64.6212 295.147 66.4216C293.727 68.322 290.684 67.7219 289.67 67.5218C271.107 63.2209 257.108 44.9169 257.108 22.9121C257.108 1.30741 265.731 -16.7965 286.627 -21.3975C287.539 -21.5976 292.408 -22.5978 294.843 -19.5971C296.973 -17.0966 296.567 -8.69477 296.567 -8.69477ZM344.649 23.0121C344.649 -5.69411 331.36 -28.8992 298.089 -28.8992C259.543 -28.8992 251.529 -5.69411 251.529 23.0121C251.529 51.7184 272.425 74.9235 298.089 74.9235C323.753 74.9235 344.649 51.6184 344.649 23.0121Z" fill="white"/><path opacity="0.1" d="M359.252 111.315C359.252 125.38 350.402 137.079 338.667 139.828C338.026 139.956 336.102 140.34 335.204 139.125C334.307 137.974 334.307 135.353 334.307 135.353V91.0487C334.307 91.0487 334.05 85.6784 335.333 84.0801C336.936 82.1622 339.95 82.8015 340.527 82.9294C353.801 85.9342 359.252 97.5058 359.252 111.315ZM332.447 91.0487V135.289C332.447 135.289 332.447 137.91 331.549 139.061C330.651 140.276 328.728 139.892 328.086 139.764C316.351 137.015 307.502 125.316 307.502 111.251C307.502 97.4418 312.952 85.8702 326.163 82.9294C326.74 82.8015 329.818 82.1622 331.357 84.0801C332.703 85.6784 332.447 91.0487 332.447 91.0487ZM362.843 111.315C362.843 92.9666 354.442 78.1345 333.409 78.1345C309.041 78.1345 303.975 92.9666 303.975 111.315C303.975 129.663 317.185 144.495 333.409 144.495C349.633 144.495 362.843 129.599 362.843 111.315Z" fill="white"/></g><defs><clipPath id="clip0"><rect width="350" height="189" fill="white"/></clipPath></defs></svg>`;


function drawBrainBackground(
	ctx: CanvasRenderingContext2D,
	W: number,
	H: number,
	brainImg: HTMLImageElement,
) {
	// Background gradient using brand colors
	const grad = ctx.createLinearGradient(0, 0, W, H);
	grad.addColorStop(0, "#0e3347");
	grad.addColorStop(0.5, "#1A4D66");
	grad.addColorStop(1, "#2C6B8A");
	ctx.fillStyle = grad;
	ctx.fillRect(0, 0, W, H);

	// Subtle radial glow
	const radGrad = ctx.createRadialGradient(W * 0.7, H * 0.4, 0, W * 0.7, H * 0.4, 400);
	radGrad.addColorStop(0, "rgba(44, 107, 138, 0.4)");
	radGrad.addColorStop(1, "rgba(44, 107, 138, 0)");
	ctx.fillStyle = radGrad;
	ctx.fillRect(0, 0, W, H);

	// Brain patterns
	const positions = [
		{ x: -40, y: 20, scale: 1.8, opacity: 0.15 },
		{ x: 180, y: -30, scale: 1.2, opacity: 0.08 },
		{ x: 600, y: 50, scale: 2.0, opacity: 0.12 },
		{ x: 750, y: -20, scale: 1.5, opacity: 0.1 },
		{ x: 400, y: H * 0.6, scale: 1.3, opacity: 0.06 },
		{ x: -80, y: H * 0.5, scale: 1.6, opacity: 0.08 },
		{ x: 850, y: H * 0.5, scale: 1.1, opacity: 0.1 },
	];

	for (const pos of positions) {
		ctx.save();
		ctx.globalAlpha = pos.opacity;
		ctx.translate(pos.x, pos.y);
		ctx.scale(pos.scale, pos.scale);
		ctx.drawImage(brainImg, 0, 0);
		ctx.restore();
	}

	ctx.globalAlpha = 1;
}

function BrandBannerMaker() {
	const canvasRef = useRef<HTMLCanvasElement>(null);
	const [generated, setGenerated] = useState(false);

	const generate = useCallback(() => {
		const canvas = canvasRef.current;
		if (!canvas) return;
		const W = 1024, H = 250;
		canvas.width = W;
		canvas.height = H;
		const ctx = canvas.getContext("2d");
		if (!ctx) return;

		// Solid primary background
		ctx.fillStyle = "#1A4D66";
		ctx.fillRect(0, 0, W, H);

		// Load logo-background SVG and draw centered
		const img = new Image();
		img.onload = () => {
			// Scale to cover the canvas height, center horizontally
			const scale = H / img.naturalHeight;
			const drawW = img.naturalWidth * scale;
			const drawH = H;
			const x = (W - drawW) / 2;
			ctx.globalAlpha = 0.15;
			ctx.drawImage(img, x, 0, drawW, drawH);
			ctx.globalAlpha = 1;
			setGenerated(true);
		};
		img.src = logoBackgroundSvg;
	}, []);

	const download = useCallback(() => {
		const canvas = canvasRef.current;
		if (!canvas) return;
		canvas.toBlob((blob) => {
			if (!blob) return;
			const url = URL.createObjectURL(blob);
			const a = document.createElement("a");
			a.href = url;
			a.download = "brand_banner_1024x250.png";
			a.click();
			setTimeout(() => URL.revokeObjectURL(url), 3000);
		}, "image/png");
	}, []);

	return (
		<div
			style={{
				maxWidth: 1100,
				margin: "20px auto 0",
				background: "#161b22",
				border: "1px solid #21262d",
				borderRadius: 16,
				padding: 24,
			}}
		>
			<div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
				<div>
					<h2 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: "#e8eaed" }}>
						Brand Banner
					</h2>
					<p style={{ margin: "4px 0 0", fontSize: 13, color: "#6e7681" }}>
						1024x250px · Primary color + logo background
					</p>
				</div>
				<div style={{ display: "flex", gap: 10 }}>
					<button
						onClick={generate}
						style={{
							padding: "9px 20px",
							borderRadius: 9,
							border: "1px solid #30363d",
							background: "transparent",
							color: "#e8eaed",
							fontSize: 13,
							fontWeight: 600,
							cursor: "pointer",
						}}
					>
						{generated ? "Regenerate" : "Generate"}
					</button>
					{generated && (
						<button
							onClick={download}
							style={{
								padding: "9px 20px",
								borderRadius: 9,
								border: "none",
								background: "#fff",
								color: "#000",
								fontSize: 13,
								fontWeight: 600,
								cursor: "pointer",
								display: "flex",
								alignItems: "center",
								gap: 7,
							}}
						>
							<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
								<path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M7 10l5 5 5-5M12 15V3" />
							</svg>
							Download PNG
						</button>
					)}
				</div>
			</div>
			<div style={{ borderRadius: 10, overflow: "hidden", border: "1px solid #21262d" }}>
				<canvas
					ref={canvasRef}
					width={1024}
					height={250}
					style={{ width: "100%", height: "auto", display: "block", background: "#0a1628" }}
				/>
			</div>
		</div>
	);
}

export function MockupsPage() {
	return (
		<>
			<MockupMaker />
			<FeaturedGraphicMaker />
			<BrandBannerMaker />
		</>
	);
}
